import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import { Modal } from 'src/components/atomsLegacy';
import { SwitchButton } from 'src/components/atomsLegacy/buttons/SwitchButton';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { UserType } from 'src/graphql';
import { useSearchUsers } from 'src/hooks';
import { FormState, MCCEventType, State } from 'src/types';
import {
  AddUserText,
  Body,
  ExteralReporterContainer,
  Footer,
  Header,
  StyledInformationBanner,
  SubTitle,
  SwitchContainer,
  TextBox,
  TextInput,
  Title,
} from '../AddUserModal/AddUserModal.styles';
import { SearchBox } from '../SearchBox';

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
  usersToExclude: (number | undefined)[];
  callback: ({
    selectedUser,
    externalName,
    currentEvent,
    internal,
  }: {
    selectedUser: UserType | null;
    externalName: string;
    currentEvent?: MCCEventType | null;
    internal: boolean;
  }) => void;
  employeeOptions?: { value: string; dataTestId: string }[];
  isPostSubmission?: boolean;
}

const employeeOptions = [
  {
    value: 'Employee directory',
    dataTestId: 'add-internal-perpetrator',
  },
  { value: 'Manually', dataTestId: 'add-external-perpetrator' },
];

export const AddPerpetratorModal = ({
  open,
  setOpen,
  onClose,
  callback,
  isPostSubmission,
  ...rest
}: Props) => {
  const [internal, setInternal] = useState(true);
  const [externalName, setExternalName] = useState('');
  const columns = ['Name', 'Role', 'Department', 'Country'];

  const currentEvent = useSelector(
    (state: State) => state.manualCaseCreation.mccReducer.currentEvent,
  );
  const internalPerpetrators = currentEvent?.perpetrators
    ?.filter((perpetrator) => !perpetrator.external && perpetrator.id)
    .map((perpetrator) => parseInt(perpetrator.id as string));

  const usersToExclude = rest.usersToExclude?.length
    ? [...rest.usersToExclude]
    : [];

  if (internalPerpetrators) {
    usersToExclude.push(...internalPerpetrators);
  }

  const { selectedUser, rows, searchUsers, setSelectedUser } = useSearchUsers(
    columns,
    usersToExclude,
  );

  // Reset details on open
  useEffect(() => {
    setInternal(true);
    setExternalName('');
    setSelectedUser(null);
    searchUsers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchUsers]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      data-testid="add-perpetrator-modal"
    >
      <Header>
        <Title>Add Named Offender</Title>
        <SwitchContainer>
          <AddUserText>Add named offender from:</AddUserText>
          <SwitchButton
            name="internal-perpetrator"
            options={rest.employeeOptions ?? employeeOptions}
            setSelectedOption={(option) => {
              setInternal(option.value === employeeOptions[0].value);
            }}
            selectedOption={
              internal ? employeeOptions[0].value : employeeOptions[1].value
            }
          />
        </SwitchContainer>
      </Header>
      <Body>
        {internal ? (
          <SearchBox
            rows={rows}
            searchUsers={searchUsers}
            columnHeaders={[]}
            testLabel={'perpetrator-search'}
            visible={open}
          />
        ) : (
          <ExteralReporterContainer>
            <SubTitle>Add named offender</SubTitle>
            <TextBox disabled={false}>
              <TextInput
                data-testid={'external-perpetrator-name'}
                placeholder={'Add named offender'}
                value={externalName}
                onChange={(event) => setExternalName(event.target.value)}
              ></TextInput>
            </TextBox>
          </ExteralReporterContainer>
        )}
      </Body>

      <Footer>
        <Button
          data-testid="save-perpetrator"
          onClick={() =>
            callback({ selectedUser, externalName, currentEvent, internal })
          }
          disabled={internal ? !selectedUser : !externalName}
          variant="primary"
          size="small"
        >
          <StyledIconInButton
            size="small"
            position="left"
            className="uil uil-user-plus"
          />
          Add named offender
        </Button>
        {isPostSubmission && (
          <StyledInformationBanner
            state={FormState.WARNING}
            title="Warning"
            description="The action of adding or linking a named offender cannot be undone once confirmed"
            data-testid="add-post-submission-offender-warning"
          />
        )}
      </Footer>
    </Modal>
  );
};
